import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, message, Radio, Select, Switch } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { extractTextContent, nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { STEP_INPUT_TYPE_MAP, StepInputTypes } from '../../../utils/TroubleshootingUtils';
import OWRichTextEditor from '../../../rich_text_editor/OWRichTextEditor';
import { connect } from 'react-redux';
import TroubleshootingStepAttachmentField, {
	validAttachment,
} from './TroubleshootingStepAttachmentField';
import TroubleshootingMultipleChoiceField from './TroubleshootingMultipleChoiceField';
import TroubleshootingGroupField from './TroubleshootingGroupField';
import { withUserType } from '../../withUserType';

const FORM_NAME = 'troubleshooting-step-form';
const FORM_FIELD_WIDTH_STYLE = { width: 600, maxWidth: 600 };

const TroubleshootingStepForm: FC<any> = ({
	step,
	onCancel,
	updatingStep,
	onSuccess,
}): React.ReactElement => {
	const [form] = useForm();

	const descriptionRef = useRef(null);

	const existingAttachments = useMemo(
		() => nullSafeGetOrElse('attachments', step, []).filter((_) => validAttachment(_)),
		[step]
	);

	const [attachments, setAttachments] = useState(existingAttachments);

	const inputType = Form.useWatch('inputType', form);

	const isUpdate = useMemo(() => !!nullSafeGet('orderingId', step), [step]);

	const title = useMemo(() => (isUpdate ? 'Save' : 'Add Step'), [isUpdate]);

	const showMultipleChoices = useMemo(
		() => inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE,
		[inputType]
	);

	const showGroupOptions = useMemo(
		() =>
			inputType === STEP_INPUT_TYPE_MAP.GROUP || inputType === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP,
		[inputType]
	);

	const getDescription = useCallback(
		() => nullSafeGet('current', descriptionRef) && descriptionRef.current.getContent(),
		[]
	);

	const initialValues = useMemo(
		() => ({
			...step,
			canProceedToNextStep: isUpdate ? step.canProceedToNextStep : true,
			inputMandatory: isUpdate ? !step.userCanSkipInput : true,
			allowMultipleAnswerSelections: step.allowMultipleAnswerSelections || false,
			title: extractTextContent(nullSafeGetOrElse('title', step, '')),
		}),
		[isUpdate, step]
	);

	const onSubmit = useCallback(
		(values) => {
			if (showMultipleChoices && nullSafeGetOrElse('multipleChoices', values, []).length < 2) {
				message.error('At least two choices are required.');
				return;
			}

			const entity = {
				stepType: 'message',
				...(step || {}),
				...values,
				inputRequired: !!values.inputType,
				userCanSkipInput: !values.inputMandatory,
				description: getDescription(),
				attachments,
			};
			onSuccess(entity);
		},
		[attachments, getDescription, onSuccess, showMultipleChoices, step]
	);

	return (
		<Form
			form={form}
			id={FORM_NAME}
			layout="vertical"
			initialValues={initialValues}
			onFinish={onSubmit}
			style={FORM_FIELD_WIDTH_STYLE}
		>
			<Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter title' }]}>
				<Input />
			</Form.Item>
			<Form.Item name="description" label="Description">
				<OWRichTextEditor
					initialEditorState={nullSafeGet('description', step)}
					editorRef={descriptionRef}
				/>
			</Form.Item>
			<TroubleshootingStepAttachmentField step={step} setAttachments={setAttachments} />
			<Form.Item
				name="canProceedToNextStep"
				label="Allowed to the next step?"
				extra="'No' will cancel work order creation and return you to previous screen"
				rules={[{ required: true, message: 'Choose an option' }]}
			>
				<Radio.Group>
					<Radio value={true}>Yes</Radio>
					<Radio value={false}>No</Radio>
				</Radio.Group>
			</Form.Item>
			<Form.Item
				label={
					<div
						className="flex flex-row items-center justify-between"
						style={FORM_FIELD_WIDTH_STYLE}
					>
						<span>User Input type</span>
						<div className="flex flex-row items-center">
							<span className={`mr-2 ${inputType ? '' : 'text-gray-300'}`}>Input Mandatory?</span>
							<Form.Item name="inputMandatory" valuePropName="checked" noStyle>
								<Switch defaultChecked={false} disabled={!inputType} size="small" />
							</Form.Item>
						</div>
					</div>
				}
				name="inputType"
			>
				<Select placeholder="None" allowClear>
					{StepInputTypes.map((f) => (
						<Select.Option key={f.value} value={f.value}>
							{f.label}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			{showMultipleChoices ? (
				<>
					<Form.Item
						name="allowMultipleAnswerSelections"
						label="User can choose multiple answers"
						rules={[{ required: true, message: 'Choose an option' }]}
					>
						<Radio.Group>
							<Radio value={true}>Yes</Radio>
							<Radio value={false}>No</Radio>
						</Radio.Group>
					</Form.Item>
					<TroubleshootingMultipleChoiceField form={form} />
				</>
			) : null}
			{showGroupOptions ? <TroubleshootingGroupField form={form} /> : null}
			<div className="flex flex-row items-center justify-end gap-2">
				<Button type="ghost" onClick={onCancel}>
					Cancel
				</Button>
				<Button type="primary" key="submit" htmlType="submit" loading={updatingStep}>
					{title}
				</Button>
			</div>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	updatingStep: state.troubleshooting_guides.creating || state.troubleshooting_guides.updating,
});

export default withUserType(connect(mapStateToProps)(TroubleshootingStepForm));
